import { Component, Input, computed, signal } from '@angular/core';
import { SatPopover } from '@zerops/zef/popover';
import { ZefError } from '@zerops/zef/errors';

interface ErrorMeta {
  error: string;
  metadata?: any;
}

interface ProcessedError {
  meta?: ErrorMeta[];
  firstMessage: string;
  hasMultipleMeta: boolean;
}

@Component({
  selector: 'z-pop-error',
  templateUrl: './pop-error.container.html',
  styleUrls: [ './pop-error.container.scss' ]
})
export class PopErrorContainer {
  #dataSignal = signal<ZefError[]>([]);

  @Input()
  set data(value: ZefError[]) {
    this.#dataSignal.set(value || []);
  }

  @Input()
  instance: SatPopover;

  /**
   * Process errors to extract meta from either:
   * 1. Direct meta property (client errors via NormalizedError)
   * 2. Nested originalError.error.error.meta (API errors from HttpErrorResponse)
   */
  processedErrors = computed<ProcessedError[]>(() => {
    return this.#dataSignal().map((error) => {
      const meta = this.#getMeta(error);
      return {
        meta,
        firstMessage: meta?.[0]?.error || error?.message || 'An error occurred',
        hasMultipleMeta: (meta?.length || 0) > 1
      };
    });
  });

  hasMultipleErrors = computed(() => {
    const errors = this.processedErrors();
    return errors.length > 1 || errors[0]?.hasMultipleMeta;
  });

  firstErrorMessage = computed(() => {
    return this.processedErrors()[0]?.firstMessage || 'An error occurred';
  });

  #getMeta(error: ZefError): ErrorMeta[] | undefined {
    if (!error) return undefined;

    // Check for direct meta first (client errors - new format)
    const normalizedError = error.originalError as any;
    if (normalizedError?.meta && Array.isArray(normalizedError.meta)) {
      return normalizedError.meta;
    }

    // Fall back to nested structure (API errors - original format)
    return error.originalError?.error?.error?.meta;
  }

  trackBy(index: number) {
    return index;
  }
}
